import {
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Container,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import useUser from "hooks/useUser";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "assets/logo.svg";
import { ReactComponent as Dashboard } from "assets/dashboard.svg";
import { ReactComponent as Campaigns } from "assets/campaigns.svg";
import { ReactComponent as Settings } from "assets/settings.svg";
import { ReactComponent as Notifications } from "assets/message.svg";
import { useState } from "react";
import { Logout } from "@mui/icons-material";
import config from "config";

const tabs = [
  {
    label: "Dashboard",
    icon: <Dashboard color="inherit" />,
    path: "/dashboard/home",
  },
  {
    label: "Campaigns",
    icon: <Campaigns color="inherit" />,
    path: "/dashboard/campaigns",
  },
  {
    label: "Notifications",
    icon: <Notifications color="inherit" />,
    path: "/dashboard/notifications",
  },
  {
    label: "Settings",
    icon: <Settings color="inherit" />,
    path: "/dashboard/settings",
  },
];

function DashboardLayout() {
  const data = useUser().data as User;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { pathname } = useLocation();
  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        "@media (max-width: 600px)": {
          gridTemplateColumns: "1fr",
        },
      }}>
      <Box
        sx={{
          backgroundColor: "#fff",
          p: 4,
          borderRight: "1px solid #e0e0e0",
          height: "100vh",
          position: "sticky",
          zIndex: 100,
          top: 0,
          maxHeight: "100vh",
          "@media (max-width: 600px)": {
            display: "none",
          },
        }}>
        <Logo width={136} />
        <Box sx={{ height: 80 }} />
        <Tabs
          orientation="vertical"
          value={pathname}
          variant="scrollable"
          sx={{
            "& .MuiTabs-indicator": {
              bgcolor: "primary.main",
              width: 4,
            },
          }}>
          {tabs.map((tab) => (
            <Tab
              sx={{
                justifyContent: "flex-start",
                fontWeight: "600",
                textTransform: "capitalize",
                minHeight: 44,
                gap: "8px",
                padding: "10px 20px",
                mb: 2,
              }}
              key={tab.path}
              value={tab.path}
              label={tab.label}
              component={Link}
              to={tab.path}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      <Box>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            p: 4,
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            zIndex: 1,
          }}>
          <Box>
            <Typography variant="h5" fontWeight="600" component="h1">
              {tabs.find((tab) => tab.path === pathname)?.label}
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleClick}
                size="small"
                sx={{ ml: 2 }}
                aria-controls={open ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}>
                <Avatar sx={{ width: 32, height: 32 }} src={data?.avatar}>
                  {data.firstName[0]}
                  {data.lastName[0]}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
              <MenuItem>
                <ListItemButton component={Link} to="/dashboard/settings">
                  <ListItemIcon>
                    <Settings fontSize="small" />
                  </ListItemIcon>
                  Settings
                </ListItemButton>
              </MenuItem>
              <Divider />
              <MenuItem>
                <ListItemButton
                  onClick={() => {
                    localStorage.removeItem(config.tokenKey);
                    window.location.href = "/";
                  }}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  Logout
                </ListItemButton>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        <Container
          maxWidth="xl"
          sx={{
            px: 0,
            pb: 8,
            "@media (min-width: 600px)": {
              px: 0,
            },
          }}>
          <Outlet />
          <BottomNavigation
            value={pathname}
            sx={{
              position: "fixed",
              bottom: 0,
              right: 0,
              width: "100%",
              "@media (min-width: 600px)": {
                display: "none",
              },
            }}>
            {tabs.map((tab) => (
              <BottomNavigationAction
                key={tab.path}
                value={tab.path}
                label={tab.label}
                icon={tab.icon}
                component={Link}
                to={tab.path}
              />
            ))}
          </BottomNavigation>
        </Container>
      </Box>
    </Box>
  );
}

export default DashboardLayout;
